/**
 * custom-ai-portrait.js
 *
 * All JavaScript for the AI Portrait product page.
 * No external libraries. No ES module syntax (no import/export).
 * Loaded with defer via the section liquid file.
 */

(function () {
  'use strict';

  /* ─────────────────────────────────────────────
     Constants
  ───────────────────────────────────────────── */

  var MAX_FILE_BYTES       = 10 * 1024 * 1024; // 10MB per API spec
  var ACCEPTED_TYPES       = ['image/jpeg', 'image/jpg', 'image/png']; // WebP not accepted by API
  var PHASE_1_TARGET      = 87;    // % to reach at end of phase 1
  var PHASE_1_DURATION_MS = 32000; // 32 s : 0 % → 87 %
  var PHASE_2_DURATION_MS = 12000; // 12 s : 87 % → 98 %
  var PROGRESS_HOLD_AT    = 98;
  var PROGRESS_COMPLETE    = 100;
  var POLL_INTERVAL_MS     = 3000;
  var MAX_POLL_ATTEMPTS    = 40;
  var UPLOAD_RETRY_DELAY   = 3000;
  var CART_EVENT_NAME      = 'cart:update';

  // Processing steps: [title, subtitle, triggerAtPercent]
  var PROCESSING_STEPS = [
    [ 'Squeezing out the perfect colours...',      3  ],
    [ 'Sketching out that signature pose...',      15 ],
    [ 'Hmm, a little more warmth here...',         28 ],
    [ 'Getting those eyes just right...',          42 ],
    [ 'Standing back for a critical squint...',    55 ],
    [ 'Final touches on the whiskers...',          68 ],
    [ 'Signing off on your masterpiece...',        82 ]
  ];

  var ART_QUOTES = [
    { text: 'Art is not what you see, but what you make others see.',     attr: 'Edgar Degas'      },
    { text: 'Every artist dips his brush in his own soul.',               attr: 'Henry Ward Beecher'},
    { text: 'Creativity takes courage.',                                  attr: 'Henri Matisse'    },
    { text: 'The purpose of art is washing the dust of daily life off our souls.', attr: 'Pablo Picasso' },
    { text: 'Art enables us to find ourselves and lose ourselves at the same time.', attr: 'Thomas Merton' }
  ];

  /* ─────────────────────────────────────────────
     Helpers
  ───────────────────────────────────────────── */

  function generateUUID() {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
      var r = (Math.random() * 16) | 0;
      var v = c === 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  }

  function easeOutExpo(t) {
    return t >= 1 ? 1 : 1 - Math.pow(2, -10 * t);
  }

  function formatMoney(cents) {
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      return window.Shopify.formatMoney(cents);
    }
    return '$' + (cents / 100).toFixed(2);
  }

  /**
   * Calculate arrival date string: 5 business days from now.
   * Skips Saturday (6) and Sunday (0).
   * @returns {string} e.g. "Mon 24 Mar"
   */
  function calcDeliveryDate() {
    var date = new Date();
    var days = 0;
    while (days < 5) {
      date.setDate(date.getDate() + 1);
      var dow = date.getDay();
      if (dow !== 0 && dow !== 6) days++;
    }
    return date.toLocaleDateString('en-AU', {
      weekday: 'short',
      day:     'numeric',
      month:   'short'
    });
  }

  /* ─────────────────────────────────────────────
     AiPortraitUploader
  ───────────────────────────────────────────── */

  function AiPortraitUploader(sectionEl) {
    this.sectionEl       = sectionEl;
    this.sectionId       = sectionEl.dataset.sectionId;
    this.awsUploadUrl    = sectionEl.dataset.awsUploadUrl    || '';
    this.apiKey          = sectionEl.dataset.apiKey          || '';
    this.pollingBaseUrl  = sectionEl.dataset.pollingBaseUrl  || '';
    this.shopDomain      = sectionEl.dataset.shopDomain      || '';
    this.processingMsg   = sectionEl.dataset.processingMessage || 'Transforming your photo...';
    this.previewMsg      = sectionEl.dataset.previewMessage    || 'Your portrait preview. Watermark removed after purchase.';

    this.sessionId       = this._getOrCreateSessionId();
    this.jobId           = sessionStorage.getItem('aiportrait_job_id')      || null;
    this.statusUrl       = null;
    this.cleanImageToken = sessionStorage.getItem('aiportrait_clean_token') || null;
    this.previewUrl      = sessionStorage.getItem('aiportrait_preview_url') || null;

    this.pollAttempts    = 0;
    this.pollTimer       = null;
    this.hasRetried      = false;
    this.progressRAF     = null;
    this.progressStart   = null;
    this.quoteTimer      = null;
    this.currentQuote    = 0;
    this.stepsTriggered  = PROCESSING_STEPS.map(function () { return false; });

    this.selectedVariantId = null;
    this.variants          = [];

    this.refs = {};
    this._cacheRefs();
    this._loadVariants();
    this._setDeliveryDate();
    this._bindEvents();

    if (this.previewUrl && this.cleanImageToken) {
      this._onPreviewReady(this.previewUrl, this.cleanImageToken, true);
    } else {
      this._setState('idle');
    }
  }

  /* ── Ref caching ──────────────────────────── */

  AiPortraitUploader.prototype._cacheRefs = function () {
    var s = this.sectionEl;
    var r = {};
    var q = function (name) { return s.querySelector('[data-ref="' + name + '"]'); };

    r.fileInput          = q('fileInput');
    r.dropZone           = q('dropZone');
    r.fileError          = q('fileError');
    r.uploaderRegion     = q('uploaderRegion');
    r.uploadPanel        = q('uploadPanel');
    r.processingPanel    = q('processingPanel');
    r.progressBar        = q('progressBar');
    r.progressFill       = q('progressFill');
    r.processingPercent  = q('processingPercent');
    r.processingSteps    = q('processingSteps');
    r.quoteText          = q('quoteText');
    r.quoteAttr          = q('quoteAttr');
    r.quoteBlock         = q('quoteBlock');
    r.uploadErrorWrap    = q('uploadErrorWrap');
    r.uploadErrorMessage = q('uploadErrorMessage');
    r.retryButton        = q('retryButton');
    r.mediaStage         = q('mediaStage');
    r.stagePlaceholder   = q('stagePlaceholder');
    r.stageShimmer       = q('stageShimmer');
    r.shimmerReveal      = q('shimmerReveal');
    r.fileBadge          = q('fileBadge');
    r.fileBadgeName      = q('fileBadgeName');
    r.previewRegion      = q('previewRegion');
    r.previewImg         = q('previewImg');
    r.previewCaption     = q('previewCaption');
    r.reuploadButton     = q('reuploadButton');
    r.variantsRegion     = q('variantsRegion');
    r.typeTabList        = q('typeTabList');
    r.variantIdInput     = q('variantIdInput');
    r.priceValue         = q('priceValue');
    r.deliveryDate       = q('deliveryDate');
    r.addToCartButton    = q('addToCartButton');
    r.cartError          = q('cartError');
    r.liveRegion         = q('liveRegion');
    r.deliveryPhysical   = q('deliveryPhysical');
    r.deliveryDigital    = q('deliveryDigital');
    r.stickyBar          = q('stickyBar');
    r.stickyPrice        = q('stickyPrice');
    r.stickyAtcButton    = q('stickyAtcButton');
    r.stickyAtcLabel     = q('stickyAtcLabel');

    this.refs = r;
  };

  /* ── Variant data ─────────────────────────── */

  AiPortraitUploader.prototype._loadVariants = function () {
    var el = document.getElementById('custom-ai-portrait-variants-' + this.sectionId);
    if (!el) return;
    try {
      this.variants = JSON.parse(el.textContent);
    } catch (e) {
      console.error('[AI Portrait] Variant JSON parse error:', e);
    }
  };

  AiPortraitUploader.prototype._findVariant = function (typeVal, sizeVal, colourVal) {
    return this.variants.find(function (v) {
      if (v.option1 !== typeVal) return false;
      if (sizeVal   && v.option2 !== sizeVal)   return false;
      if (colourVal && v.option3 !== colourVal) return false;
      return true;
    }) || null;
  };

  /* ── Delivery date ────────────────────────── */

  AiPortraitUploader.prototype._setDeliveryDate = function () {
    if (this.refs.deliveryDate) {
      this.refs.deliveryDate.textContent = calcDeliveryDate();
    }
    // Apply correct delivery message for the initially selected tab on load
    var firstTab = this.refs.typeTabList &&
      this.refs.typeTabList.querySelector('[role="tab"]');
    if (firstTab) {
      var isDigital = firstTab.dataset.typeValue === 'Digital';
      this._toggle(this.refs.deliveryPhysical, !isDigital);
      this._toggle(this.refs.deliveryDigital,   isDigital);
    }
  };

  /* ── Session ID ───────────────────────────── */

  AiPortraitUploader.prototype._getOrCreateSessionId = function () {
    var id = sessionStorage.getItem('aiportrait_session_id');
    if (!id) {
      id = generateUUID();
      sessionStorage.setItem('aiportrait_session_id', id);
    }
    return id;
  };

  /* ── Event binding ────────────────────────── */

  AiPortraitUploader.prototype._bindEvents = function () {
    var self = this;
    var r    = this.refs;

    if (r.fileInput) {
      r.fileInput.addEventListener('change', function (e) {
        var file = e.target.files && e.target.files[0];
        if (file) self._handleFileSelect(file);
      });
    }

    if (r.dropZone && window.matchMedia('(pointer: fine)').matches) {
      r.dropZone.addEventListener('dragover', function (e) {
        e.preventDefault();
        r.dropZone.classList.add('is-drag-over');
      });
      r.dropZone.addEventListener('dragleave', function () {
        r.dropZone.classList.remove('is-drag-over');
      });
      r.dropZone.addEventListener('drop', function (e) {
        e.preventDefault();
        r.dropZone.classList.remove('is-drag-over');
        var file = e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files[0];
        if (file) self._handleFileSelect(file);
      });
    }

    if (r.retryButton) {
      r.retryButton.addEventListener('click', function () { self._resetToIdle(); });
    }

    if (r.reuploadButton) {
      r.reuploadButton.addEventListener('click', function () { self._resetToIdle(); });
    }

    if (r.addToCartButton) {
      r.addToCartButton.addEventListener('click', function () { self._addToCart(); });
    }

    if (r.stickyAtcButton) {
      r.stickyAtcButton.addEventListener('click', function () { self._addToCart(); });
    }

    // Show sticky bar when main ATC button scrolls out of view
    if (r.addToCartButton && r.stickyBar && 'IntersectionObserver' in window) {
      var stickyObserver = new IntersectionObserver(function (entries) {
        var visible = !entries[0].isIntersecting;
        r.stickyBar.classList.toggle('aip-sticky-bar--visible', visible);
        r.stickyBar.setAttribute('aria-hidden', visible ? 'false' : 'true');
      }, { threshold: 0 });
      stickyObserver.observe(r.addToCartButton);
    }

    // Frame colour swatches (Framed format) — drives the preview mockup
    // overlay and is sent as a line-item property on add-to-cart.
    this.frameColour = 'Black';
    var frameSwatches = this.sectionEl.querySelectorAll('input[data-ref="frameColourInput"]');
    frameSwatches.forEach(function (input) {
      if (input.checked) self.frameColour = input.value;
      input.addEventListener('change', function () {
        self.frameColour = input.value;
        var lbl = self.sectionEl.querySelector('[data-ref="frameColourLabel"]');
        if (lbl) lbl.textContent = input.value;
        var group = input.closest('[role="radiogroup"]');
        if (group) {
          group.querySelectorAll('label[role="radio"]').forEach(function (l) {
            var inp = l.querySelector('input');
            l.setAttribute('aria-checked', inp && inp.checked ? 'true' : 'false');
          });
        }
        self._updateMockup();
        self._reselectFramedVariant();
      });
    });

    if (r.typeTabList) {
      r.typeTabList.addEventListener('click', function (e) {
        var tab = e.target.closest('[role="tab"]');
        if (tab) self._selectTypeTab(tab);
      });
      r.typeTabList.addEventListener('keydown', function (e) {
        self._handleTabKeydown(e);
      });
    }

    if (r.variantsRegion) {
      r.variantsRegion.addEventListener('change', function (e) {
        var target = e.target;
        if (target && target.name && target.name.indexOf('aip-size-') === 0) {
          self._handleSizeChange(target);
        }
      });
      r.variantsRegion.addEventListener('keydown', function (e) {
        self._handleSizeKeydown(e);
      });
    }
  };

  /* ── State machine ────────────────────────── */

  AiPortraitUploader.prototype._setState = function (state) {
    this.sectionEl.dataset.state = state;

    var r    = this.refs;
    var idle = state === 'idle';
    var proc = state === 'processing';
    var prev = state === 'preview';
    var err  = state === 'error';

    // Uploader region (wraps media stage + interaction zone)
    this._toggle(r.uploaderRegion, idle || proc || err);

    // Within uploader: panels
    this._toggle(r.uploadPanel,    idle || err);
    this._toggle(r.processingPanel, proc);
    this._toggle(r.uploadErrorWrap, err);

    // Media stage
    this._toggle(r.stagePlaceholder, idle || err);
    this._toggle(r.stageShimmer,     proc);

    // Preview region
    this._toggle(r.previewRegion, prev);

    // Variants
    if (r.variantsRegion) {
      r.variantsRegion.setAttribute('aria-disabled', proc ? 'true' : 'false');
      r.variantsRegion.querySelectorAll('input, button').forEach(function (el) {
        if (proc) el.setAttribute('disabled', '');
        else el.removeAttribute('disabled');
      });
    }

    if (r.addToCartButton) {
      r.addToCartButton.disabled = !prev;
      r.addToCartButton.setAttribute('aria-disabled', prev ? 'false' : 'true');
      if (r.addToCartLabel) {
        if (proc) {
          r.addToCartLabel.textContent = 'Creating your portrait\u2026';
        } else if (prev) {
          r.addToCartLabel.textContent = this.atcLabel;
        } else {
          r.addToCartLabel.textContent = 'Upload your photo to continue';
        }
      }
    }

    if (r.stickyAtcButton) {
      r.stickyAtcButton.disabled = !prev;
      r.stickyAtcButton.setAttribute('aria-disabled', prev ? 'false' : 'true');
    }
    if (r.stickyAtcLabel) {
      if (proc) {
        r.stickyAtcLabel.textContent = 'Creating your portrait\u2026';
      } else if (prev) {
        r.stickyAtcLabel.textContent = this.atcLabel || 'Add to Cart';
      } else {
        r.stickyAtcLabel.textContent = 'Upload your photo to continue';
      }
    }
  };

  AiPortraitUploader.prototype._toggle = function (el, visible) {
    if (!el) return;
    el.classList.toggle('hidden', !visible);
  };

  /* ── Reset to idle ────────────────────────── */

  AiPortraitUploader.prototype._resetToIdle = function () {
    sessionStorage.removeItem('aiportrait_job_id');
    sessionStorage.removeItem('aiportrait_clean_token');
    sessionStorage.removeItem('aiportrait_preview_url');

    this.jobId           = null;
    this.statusUrl       = null;
    this.cleanImageToken = null;
    this.previewUrl      = null;
    this.hasRetried      = false;
    this.pollAttempts    = 0;
    this.stepsTriggered  = PROCESSING_STEPS.map(function () { return false; });

    if (this.pollTimer)   { clearTimeout(this.pollTimer);           this.pollTimer   = null; }
    if (this.progressRAF) { cancelAnimationFrame(this.progressRAF); this.progressRAF = null; }
    if (this.quoteTimer)  { clearInterval(this.quoteTimer);         this.quoteTimer  = null; }

    if (this.refs.fileInput)  this.refs.fileInput.value = '';
    if (this.refs.previewImg) this.refs.previewImg.src  = '';

    // Reset shimmer
    if (this.refs.shimmerReveal) this.refs.shimmerReveal.style.opacity = '0';

    // Reset steps
    this._resetSteps();
    this._setProgressFill(0);
    this._clearFileError();

    this._setState('idle');
    this._announce('Upload zone ready. Please upload a photo to begin.');
  };

  /* ── File selection ───────────────────────── */

  AiPortraitUploader.prototype._handleFileSelect = function (file) {
    if (ACCEPTED_TYPES.indexOf(file.type) === -1) {
      this._showFileError('Please upload a JPEG or PNG image.');
      return;
    }
    if (file.size > MAX_FILE_BYTES) {
      this._showFileError('That file is too large. Please upload an image under 20\u00a0MB.');
      return;
    }

    this._clearFileError();

    // Show file badge in media stage
    if (this.refs.fileBadgeName) {
      this.refs.fileBadgeName.textContent = file.name;
    }

    this._uploadImage(file, false);
  };

  AiPortraitUploader.prototype._showFileError = function (msg) {
    if (!this.refs.fileError) return;
    this.refs.fileError.textContent = msg;
    this.refs.fileError.classList.remove('hidden');
    this._announce(msg);
  };

  AiPortraitUploader.prototype._clearFileError = function () {
    if (!this.refs.fileError) return;
    this.refs.fileError.textContent = '';
    this.refs.fileError.classList.add('hidden');
  };

  /* ── Upload ───────────────────────────────── */

  AiPortraitUploader.prototype._uploadImage = function (file, isRetry) {
    var self = this;

    if (!isRetry) {
      this._setState('processing');
      this._startProgressBar();
      this._startQuoteRotator();
      this._announce(this.processingMsg);
      // Show file badge
      if (this.refs.fileBadge) this.refs.fileBadge.classList.remove('hidden');
    }

    var form = new FormData();
    form.append('image', file);
    // API only accepts the image field — session and shop data stored client-side only

    var uploadHeaders = {};
    if (this.apiKey) uploadHeaders['X-Api-Key'] = this.apiKey;

    fetch(this.awsUploadUrl, { method: 'POST', headers: uploadHeaders, body: form })
      .then(function (res) {
        if (res.status === 429) {
          var retryAfter = res.headers.get('Retry-After') || res.headers.get('X-RateLimit-Reset');
          throw new Error('rate_limited:' + (retryAfter || '60'));
        }
        if (!res.ok) throw new Error('HTTP ' + res.status);
        return res.json();
      })
      .then(function (data) {
        // Support both camelCase (jobId) and snake_case (job_id) response formats
        var jobId = (data && (data.jobId || data.job_id)) || null;
        if (!jobId) throw new Error('No jobId in response');
        self.jobId     = jobId;
        self.statusUrl = (data && data.statusUrl) || null;
        sessionStorage.setItem('aiportrait_job_id', jobId);
        self._startPolling();
      })
      .catch(function (err) {
        console.error('[AI Portrait] Upload error:', err);
        var msg = err.message || '';
        if (msg.indexOf('rate_limited:') === 0) {
          var waitSec = parseInt(msg.split(':')[1], 10) || 60;
          self._onError('Too many uploads. Please wait ' + waitSec + ' seconds and try again.');
          return;
        }
        if (!isRetry && !self.hasRetried) {
          self.hasRetried = true;
          setTimeout(function () { self._uploadImage(file, true); }, UPLOAD_RETRY_DELAY);
        } else {
          self._onError('Something went wrong uploading your photo. Please try again.');
        }
      });
  };

  /* ── Progress bar ─────────────────────────── */

  AiPortraitUploader.prototype._startProgressBar = function () {
    var self = this;
    this._setProgressFill(0);
    this.progressStart = null;

    var step = function (ts) {
      if (!self.progressStart) self.progressStart = ts;
      var elapsed = ts - self.progressStart;
      var pct;

      if (elapsed < PHASE_1_DURATION_MS) {
        // Phase 1: linear 0 % → 87 % over 32 s
        pct = (elapsed / PHASE_1_DURATION_MS) * PHASE_1_TARGET;
      } else {
        // Phase 2: linear 87 % → 98 % over 12 s, then hold
        var phase2Elapsed = Math.min(elapsed - PHASE_1_DURATION_MS, PHASE_2_DURATION_MS);
        pct = PHASE_1_TARGET + (phase2Elapsed / PHASE_2_DURATION_MS) * (PROGRESS_HOLD_AT - PHASE_1_TARGET);
      }

      self._setProgressFill(pct);
      self._tickSteps(pct);

      if (elapsed < PHASE_1_DURATION_MS + PHASE_2_DURATION_MS) {
        self.progressRAF = requestAnimationFrame(step);
      }
    };
    this.progressRAF = requestAnimationFrame(step);
  };

  AiPortraitUploader.prototype._setProgressFill = function (value) {
    var pct = Math.round(value);
    if (this.refs.progressFill)      this.refs.progressFill.style.width = pct + '%';
    if (this.refs.progressBar)       this.refs.progressBar.setAttribute('aria-valuenow', pct);
    if (this.refs.processingPercent) this.refs.processingPercent.textContent = pct + '%';
    // Drive subtle shimmer opacity: grows from 0 to 0.35 as progress advances
    if (this.refs.shimmerReveal) {
      var opacity = Math.min(pct / PROGRESS_HOLD_AT * 0.35, 0.35);
      this.refs.shimmerReveal.style.opacity = opacity.toString();
    }
  };

  AiPortraitUploader.prototype._completeProgressBar = function (cb) {
    var self  = this;
    var start = null;
    var from  = PROGRESS_HOLD_AT;
    var dur   = 400;

    if (this.progressRAF) { cancelAnimationFrame(this.progressRAF); this.progressRAF = null; }

    var step = function (ts) {
      if (!start) start = ts;
      var t   = Math.min((ts - start) / dur, 1);
      var val = from + (PROGRESS_COMPLETE - from) * t;
      self._setProgressFill(val);
      if (t < 1) {
        self.progressRAF = requestAnimationFrame(step);
      } else if (typeof cb === 'function') {
        cb();
      }
    };
    this.progressRAF = requestAnimationFrame(step);
  };

  /* ── Processing steps ─────────────────────── */

  AiPortraitUploader.prototype._tickSteps = function (pct) {
    var self  = this;
    var panel = this.refs.processingSteps;
    if (!panel) return;

    var lastIdx = PROCESSING_STEPS.length - 1;

    PROCESSING_STEPS.forEach(function (step, i) {
      if (!self.stepsTriggered[i] && pct >= step[1]) {
        self.stepsTriggered[i] = true;
        var stepEl = panel.querySelector('[data-step="' + i + '"]');
        if (!stepEl) return;

        // Mark previous step as done first
        if (i > 0) {
          var prevEl = panel.querySelector('[data-step="' + (i - 1) + '"]');
          if (prevEl) prevEl.dataset.state = 'done';
        }
        stepEl.dataset.state = 'active';
      }
    });

    // Ensure last step activates by 95% regardless — progress holds at 98% until image is ready
    if (pct >= 95 && !self.stepsTriggered[lastIdx]) {
      self.stepsTriggered[lastIdx] = true;
      var lastEl = panel.querySelector('[data-step="' + lastIdx + '"]');
      if (lastEl) lastEl.dataset.state = 'active';
      var prevEl = panel.querySelector('[data-step="' + (lastIdx - 1) + '"]');
      if (prevEl) prevEl.dataset.state = 'done';
    }
  };

  AiPortraitUploader.prototype._resetSteps = function () {
    var panel = this.refs.processingSteps;
    if (!panel) return;
    panel.querySelectorAll('.aip-step').forEach(function (el) {
      el.dataset.state = 'pending';
    });
    this.stepsTriggered = PROCESSING_STEPS.map(function () { return false; });
  };

  /* ── Quote rotator ────────────────────────── */

  AiPortraitUploader.prototype._startQuoteRotator = function () {
    var self = this;
    this.currentQuote = Math.floor(Math.random() * ART_QUOTES.length);
    this._showQuote(this.currentQuote);

    this.quoteTimer = setInterval(function () {
      self.currentQuote = (self.currentQuote + 1) % ART_QUOTES.length;
      self._showQuote(self.currentQuote);
    }, 8000);
  };

  AiPortraitUploader.prototype._showQuote = function (index) {
    var q    = ART_QUOTES[index];
    var tEl  = this.refs.quoteText;
    var aEl  = this.refs.quoteAttr;
    var wrap = this.refs.quoteBlock;
    if (!tEl || !aEl) return;

    if (wrap) wrap.classList.add('is-fading');
    var self = this;
    setTimeout(function () {
      tEl.textContent = '\u201c' + q.text + '\u201d';
      aEl.textContent = '\u2014 ' + q.attr;
      if (wrap) wrap.classList.remove('is-fading');
    }, 300);
  };

  /* ── Polling ──────────────────────────────── */

  AiPortraitUploader.prototype._startPolling = function () {
    this.pollAttempts = 0;
    this._poll();
  };

  AiPortraitUploader.prototype._poll = function () {
    var self = this;

    if (this.pollAttempts >= MAX_POLL_ATTEMPTS) {
      this._handlePollTimeout();
      return;
    }

    // Prefer the statusUrl returned directly by the upload response, then fall back to
    // constructing the URL from pollingBaseUrl + {jobId} placeholder, or legacy query string.
    var url;
    if (this.statusUrl) {
      // statusUrl may be a path only — prepend the base origin if needed
      url = this.statusUrl.indexOf('http') === 0
        ? this.statusUrl
        : this.pollingBaseUrl.replace(/\/v1\/.*/, '') + this.statusUrl;
    } else if (this.pollingBaseUrl.indexOf('{jobId}') !== -1) {
      url = this.pollingBaseUrl.replace('{jobId}', encodeURIComponent(this.jobId));
    } else {
      url = this.pollingBaseUrl + '/result?job_id=' + encodeURIComponent(this.jobId);
    }

    var pollHeaders = { 'Accept': 'application/json' };
    if (this.apiKey) pollHeaders['X-Api-Key'] = this.apiKey;

    fetch(url, { headers: pollHeaders })
      .then(function (res) {
        if (res.status === 429) {
          // Rate limited during polling — wait and retry
          self.pollTimer = setTimeout(function () { self._poll(); }, 60000);
          return null;
        }
        if (!res.ok) throw new Error('Poll HTTP ' + res.status);
        return res.json();
      })
      .then(function (data) {
        self.pollAttempts++;
        // Normalise API response — supports 'state' (Completed/Processing/Failed)
        // and legacy 'status' (complete/processing/error) field names
        // Normalise state — API uses PascalCase: Queued, Processing, Completed, Failed
        var state = (data.state || data.status || '').toLowerCase();

        if (state === 'completed' || state === 'complete') {
          // resultUrl is the watermarked CDN image returned on completion
          var imageUrl  = data.resultUrl || data.watermarked_image_url || '';
          var cleanToken = data.cleanImageToken || data.clean_image_token || imageUrl;
          self._onPreviewReady(imageUrl, cleanToken, false);

        } else if (state === 'queued') {
          // Job created and waiting in queue — keep polling
          self.pollTimer = setTimeout(function () { self._poll(); }, POLL_INTERVAL_MS);

        } else if (state === 'processing') {
          // Worker picked up the job and is actively processing — keep polling
          self.pollTimer = setTimeout(function () { self._poll(); }, POLL_INTERVAL_MS);

        } else if (state === 'failed' || state === 'error') {
          // errorMessage contains the failure details from the API
          self._handlePollError(data.errorMessage || data.message || 'Processing failed. Please try again.');

        } else {
          // Unknown state — keep polling conservatively
          self.pollTimer = setTimeout(function () { self._poll(); }, POLL_INTERVAL_MS);
        }
      })
      .catch(function (err) {
        console.error('[AI Portrait] Poll error:', err);
        self.pollAttempts++;
        self.pollTimer = setTimeout(function () { self._poll(); }, POLL_INTERVAL_MS);
      });
  };

  AiPortraitUploader.prototype._handlePollTimeout = function () {
    if (!this.hasRetried) {
      this.hasRetried = true; this.pollAttempts = 0; this._poll();
    } else {
      this._onError('Your portrait is taking longer than expected. Please try again.');
    }
  };

  AiPortraitUploader.prototype._handlePollError = function (msg) {
    if (!this.hasRetried) {
      this.hasRetried = true; this.pollAttempts = 0; this._poll();
    } else {
      this._onError(msg || 'Something went wrong generating your portrait. Please try again.');
    }
  };

  /* ── Preview ready ────────────────────────── */

  AiPortraitUploader.prototype._onPreviewReady = function (watermarkedUrl, cleanToken, skipAnim) {
    var self = this;

    this.cleanImageToken = cleanToken;
    this.previewUrl      = watermarkedUrl;
    sessionStorage.setItem('aiportrait_clean_token', cleanToken);
    sessionStorage.setItem('aiportrait_preview_url', watermarkedUrl);

    if (this.quoteTimer) { clearInterval(this.quoteTimer); this.quoteTimer = null; }

    var reveal = function () {
      if (self.refs.previewImg) {
        self.refs.previewImg.src = watermarkedUrl;
        self.refs.previewImg.alt = 'Your portrait preview';
      }
      if (self.refs.previewCaption) {
        self.refs.previewCaption.textContent = self.previewMsg;
      }
      self._setState('preview');
      self._initVariantSelection();
      self._announce(self.previewMsg);
    };

    if (skipAnim) {
      reveal();
    } else {
      this._completeProgressBar(reveal);
    }
  };

  /* ── Error state ──────────────────────────── */

  AiPortraitUploader.prototype._onError = function (msg) {
    if (this.pollTimer)   { clearTimeout(this.pollTimer);           this.pollTimer   = null; }
    if (this.progressRAF) { cancelAnimationFrame(this.progressRAF); this.progressRAF = null; }
    if (this.quoteTimer)  { clearInterval(this.quoteTimer);         this.quoteTimer  = null; }

    if (this.refs.uploadErrorMessage) {
      this.refs.uploadErrorMessage.textContent = msg || 'Something went wrong. Please try again.';
    }
    this._setState('error');
    this._announce(msg || 'An error occurred. Please try again.');
  };

  /* ── Variant selection ────────────────────── */

  AiPortraitUploader.prototype._initVariantSelection = function () {
    var firstTab = this.refs.typeTabList &&
      this.refs.typeTabList.querySelector('[role="tab"]');
    if (firstTab) this._selectTypeTab(firstTab);
  };

  AiPortraitUploader.prototype._selectTypeTab = function (tab) {
    var self    = this;
    var tabList = this.refs.typeTabList;
    if (!tabList) return;

    tabList.querySelectorAll('[role="tab"]').forEach(function (t) {
      var selected = t === tab;
      t.setAttribute('aria-selected', selected ? 'true' : 'false');
      t.tabIndex = selected ? 0 : -1;
    });

    var typeValue = tab.dataset.typeValue;
    var panelId   = tab.dataset.panelId;
    var isDigital = typeValue === 'Digital';

    // Track current format + swap the preview mockup overlay
    this.currentType = typeValue;
    this._updateMockup();

    // Swap delivery message for Digital vs physical formats
    self._toggle(self.refs.deliveryPhysical, !isDigital);
    self._toggle(self.refs.deliveryDigital,   isDigital);

    self.sectionEl.querySelectorAll('[role="tabpanel"]').forEach(function (p) {
      if (p.id === panelId) p.removeAttribute('hidden');
      else p.setAttribute('hidden', '');
    });

    if (typeValue === 'Digital') {
      var v = this._findVariant('Digital', null);
      if (v) this._selectVariant(v);
      return;
    }

    var activePanel = panelId ? document.getElementById(panelId) : null;
    if (activePanel) {
      var firstInput = activePanel.querySelector('.aip-size-buttons input[type="radio"]');
      if (firstInput) {
        firstInput.checked = true;
        this._handleSizeChange(firstInput);
      }
    }
  };

  AiPortraitUploader.prototype._handleSizeChange = function (input) {
    var typeVal = input.dataset.typeValue;
    // Framed variants differ by option3 (frame colour); without it the first
    // matching colour (Black) would always be selected.
    var colourVal = typeVal === 'Framed' ? (this.frameColour || 'Black') : null;
    var variant = this._findVariant(typeVal, input.dataset.sizeValue, colourVal);
    if (variant) this._selectVariant(variant);

    var group = input.closest('[role="radiogroup"]');
    if (group) {
      group.querySelectorAll('label[role="radio"]').forEach(function (lbl) {
        var inp = lbl.querySelector('input[type="radio"]');
        var checked = inp && inp.checked;
        lbl.setAttribute('aria-checked', checked ? 'true' : 'false');
        lbl.tabIndex = checked ? 0 : -1;
      });
    }
  };

  /**
   * Re-run variant selection for the currently checked Framed size after the
   * frame colour changes, so the cart gets the variant matching the swatch.
   */
  AiPortraitUploader.prototype._reselectFramedVariant = function () {
    if (this.currentType !== 'Framed') return;
    var checked = this.sectionEl.querySelector(
      '.aip-size-panel[data-type-value="Framed"] .aip-size-buttons input[type="radio"]:checked');
    if (checked) this._handleSizeChange(checked);
  };

  AiPortraitUploader.prototype._selectVariant = function (variant) {
    this.selectedVariantId = variant.id;
    if (this.refs.variantIdInput) this.refs.variantIdInput.value = variant.id;
    if (this.refs.priceValue)     this.refs.priceValue.textContent = formatMoney(variant.price);
    if (this.refs.stickyPrice)    this.refs.stickyPrice.textContent = formatMoney(variant.price);
    // Show format check icon once a selection is made
    var checkIcon = this.sectionEl.querySelector('[data-ref="formatCheckIcon"]');
    if (checkIcon) checkIcon.classList.remove('hidden');
  };

  /* ── Keyboard navigation ──────────────────── */

  AiPortraitUploader.prototype._handleTabKeydown = function (e) {
    var tabList = this.refs.typeTabList;
    if (!tabList) return;
    var tabs    = Array.prototype.slice.call(tabList.querySelectorAll('[role="tab"]'));
    var current = tabs.indexOf(document.activeElement);
    if (current === -1) return;

    var next = -1;
    if      (e.key === 'ArrowRight' || e.key === 'ArrowDown')  next = (current + 1) % tabs.length;
    else if (e.key === 'ArrowLeft'  || e.key === 'ArrowUp')    next = (current - 1 + tabs.length) % tabs.length;
    else if (e.key === 'Home')                                  next = 0;
    else if (e.key === 'End')                                   next = tabs.length - 1;

    if (next !== -1) { e.preventDefault(); tabs[next].focus(); this._selectTypeTab(tabs[next]); }
  };

  AiPortraitUploader.prototype._handleSizeKeydown = function (e) {
    var label = e.target.closest('label[role="radio"]');
    if (!label) return;
    var group  = label.closest('[role="radiogroup"]');
    if (!group) return;
    var labels = Array.prototype.slice.call(group.querySelectorAll('label[role="radio"]'));
    var idx    = labels.indexOf(label);
    if (idx === -1) return;

    var next = -1;
    if      (e.key === 'ArrowRight' || e.key === 'ArrowDown') next = (idx + 1) % labels.length;
    else if (e.key === 'ArrowLeft'  || e.key === 'ArrowUp')   next = (idx - 1 + labels.length) % labels.length;

    if (next !== -1) {
      e.preventDefault();
      var nextInput = labels[next].querySelector('input[type="radio"]');
      if (nextInput) {
        nextInput.checked = true;
        nextInput.dispatchEvent(new Event('change', { bubbles: true }));
        labels[next].focus();
      }
    }
  };

  /* ── Preview mockup (frame/canvas overlay) ── */

  AiPortraitUploader.prototype._updateMockup = function () {
    var mode = 'digital';
    if (this.currentType === 'Canvas') {
      mode = 'canvas';
    } else if (this.currentType === 'Framed') {
      mode = 'framed-' + String(this.frameColour || 'Black').toLowerCase();
    }
    this.sectionEl.dataset.mockup = mode;
  };

  /* ── Add to Cart ──────────────────────────── */

  AiPortraitUploader.prototype._addToCart = function () {
    var self = this;
    var r    = this.refs;

    if (!this.selectedVariantId) {
      this._showCartError('Please select a format to continue.');
      return;
    }
    if (!this.cleanImageToken) {
      this._showCartError('Your portrait is not ready yet. Please wait.');
      return;
    }

    if (r.addToCartButton) {
      r.addToCartButton.disabled = true;
      r.addToCartButton.setAttribute('aria-disabled', 'true');
      r.addToCartButton.classList.add('is-loading');
    }
    this._clearCartError();

    var payload = {
      id:       this.selectedVariantId,
      quantity: 1,
      properties: {
        '_job_id':            this.jobId            || '',
        '_clean_image_token': this.cleanImageToken,
        '_session_id':        this.sessionId,
        // Underscore prefix keeps this hidden from the cart + checkout UI, but the theme
        // still reads it (cart-products.liquid) to render the generated art as the thumbnail.
        '_preview_image':     this.previewUrl       || '',
        '_shop_domain':       this.shopDomain
      }
    };

    // Frame colour — visible on the order as a line-item property
    if (this.currentType === 'Framed' && this.frameColour) {
      payload.properties['Frame colour'] = this.frameColour;
    }

    fetch('/cart/add.js', {
      method:  'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body:    JSON.stringify(payload)
    })
      .then(function (res) {
        if (!res.ok) {
          return res.json().then(function (err) { throw new Error(err.message || 'Could not add to cart.'); });
        }
        return res.json();
      })
      .then(function () {
        if (r.addToCartButton) {
          r.addToCartButton.disabled = false;
          r.addToCartButton.setAttribute('aria-disabled', 'false');
          r.addToCartButton.classList.remove('is-loading');
        }
        // Dispatch cart:update so Horizon updates the cart count and item list
        document.dispatchEvent(new CustomEvent(CART_EVENT_NAME, { bubbles: true, detail: { resource: {}, data: {} } }));

        // Directly open the cart drawer — works regardless of the auto-open theme setting
        var cartDrawer = document.querySelector('cart-drawer-component');
        if (cartDrawer && typeof cartDrawer.open === 'function') {
          cartDrawer.open();
        } else if (cartDrawer && typeof cartDrawer.showDialog === 'function') {
          cartDrawer.showDialog();
        }

        self._announce('Added to cart successfully.');
      })
      .catch(function (err) {
        console.error('[AI Portrait] Cart error:', err);
        if (r.addToCartButton) {
          r.addToCartButton.disabled = false;
          r.addToCartButton.setAttribute('aria-disabled', 'false');
          r.addToCartButton.classList.remove('is-loading');
        }
        self._showCartError(err.message || 'Could not add to cart. Please try again.');
      });
  };

  AiPortraitUploader.prototype._showCartError = function (msg) {
    if (!this.refs.cartError) return;
    this.refs.cartError.textContent = msg;
    this.refs.cartError.classList.remove('hidden');
  };

  AiPortraitUploader.prototype._clearCartError = function () {
    if (!this.refs.cartError) return;
    this.refs.cartError.textContent = '';
    this.refs.cartError.classList.add('hidden');
  };

  AiPortraitUploader.prototype._announce = function (msg) {
    var el = this.refs.liveRegion;
    if (!el) return;
    el.textContent = '';
    setTimeout(function () { el.textContent = msg; }, 50);
  };

  /* ── Bootstrap ────────────────────────────── */

  /* ── Reviews "See more" ───────────────────── */

  function initReviewsShowMore() {
    var btn = document.querySelector('[data-ref="reviewsMoreBtn"]');
    if (!btn) return;

    btn.addEventListener('click', function () {
      var nextBatch = parseInt(btn.dataset.nextBatch, 10);

      // Reveal cards belonging to this batch with staggered fade-in
      var cards = document.querySelectorAll('[data-review-batch="' + nextBatch + '"]');
      cards.forEach(function (card, i) {
        // Swap data-src → src now to trigger the fetch, right as the card becomes visible
        var img = card.querySelector('img[data-src]');
        if (img) {
          img.src = img.dataset.src;
          img.removeAttribute('data-src');
        }

        card.classList.remove('aip-review-card--hidden');
        // Stagger each card by 80ms so they cascade in left-to-right
        setTimeout(function () {
          card.classList.add('aip-review-card--revealing');
        }, i * 80);
      });

      // Advance to next batch or hide button when all shown
      if (nextBatch >= 4) {
        var wrap = document.querySelector('[data-ref="reviewsMoreWrap"]');
        if (wrap) wrap.style.display = 'none';
      } else {
        btn.dataset.nextBatch = nextBatch + 1;
      }
    });
  }

  function init() {
    document.querySelectorAll('[data-section-type="custom-ai-portrait"]')
      .forEach(function (el) { new AiPortraitUploader(el); });
    initReviewsShowMore();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
